import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import api from '../../lib/api';
import toast from 'react-hot-toast';

interface AddTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  sportId: string;
  sportDisplayName: string;
  onSuccess: () => void;
}

const LEVEL_SUGGESTIONS = [
  'Program',
  'Varsity',
  'JV',
  'Freshman',
  'Club',
  '7th Grade',
  '8th Grade',
];

export const AddTeamModal = ({
  isOpen,
  onClose,
  organizationId,
  sportId,
  sportDisplayName,
  onSuccess,
}: AddTeamModalProps) => {
  const [level, setLevel] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLevel('');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!level.trim()) {
      setError('Team level is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const res = await api.post(
        `/api/organizations/${organizationId}/sports/${sportId}/teams`,
        { level: level.trim() }
      );

      const reactivated = res.data?.reactivated === true;
      toast.success(
        reactivated
          ? `Re-added existing ${level.trim()} team`
          : `Added ${level.trim()} team`
      );
      onSuccess();
      onClose();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to create team';
      if (err.response?.status === 409) {
        setError('A team with this level already exists for this sport');
      } else {
        setError(message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
          <form onSubmit={handleSubmit}>
            <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Add Team
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Adding team to <span className="font-medium text-gray-900 dark:text-gray-100">{sportDisplayName}</span>
              </p>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-800 dark:text-red-300 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="team-level" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Team Level <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="team-level"
                  list="level-suggestions"
                  value={level}
                  onChange={(e) => setLevel(e.target.value)}
                  className="input"
                  placeholder="e.g., Varsity"
                  disabled={isSaving}
                  autoFocus
                />
                <datalist id="level-suggestions">
                  {LEVEL_SUGGESTIONS.map((l) => (
                    <option key={l} value={l} />
                  ))}
                </datalist>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSaving || !level.trim()}
              >
                {isSaving ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    Adding...
                  </>
                ) : (
                  'Add Team'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
